/**
 * DashboardWidgets.js
 *
 * The full-width *panel* widgets a user can pin to the Manager dashboard
 * (`block.*` ids in {@link module:config/dashboardWidgets}).
 *
 * Two families live here and they differ in where their data comes from:
 *
 *  - **List panels** (recent orders / open work orders / quotes / leads / low
 *    stock) read the datasets `ManagementDataProvider` already polls, so they
 *    cost NOTHING extra — they are a projection of data the dashboard holds.
 *  - **Workspace panels** (time tracker, meeting transcripts) own their own
 *    fetch, because that data is personal rather than store-wide and the
 *    provider deliberately does not carry it. Both fetch ONLY while pinned:
 *    the component is not mounted when the widget is off, so a user who does
 *    not want the panel never pays for its request.
 *
 * Every panel is fail-soft: an error or an empty dataset renders the premium
 * empty state, never a spinner that never resolves and never a crash.
 */

import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ActivityIndicator, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import Icon from './SafeIcon';
import {useManagementTheme, formatCurrency, formatNumber} from '../../helpers/managementTheme';
import {ChartCard} from './ManagementCharts';
import {EmptyState, ListRow, SectionTitle, StatusBadge} from './ManagementUI';
import {RankedRow} from '../premium/PremiumKit';
import {fetchTimeSummary, fetchTrackerState} from '../../helpers/timeTrackingApi';
import {formatTrackerClock, formatTrackerDuration, resolveTrackerElapsedSeconds, resolveTrackerLabels, resolveTrackerState} from '../../helpers/timeTrackerFormat';
import {listMeetings} from '../../services/meetingTranscriptApi';

/** How many rows a list panel shows before deferring to its full screen. */
const PANEL_ROWS = 4;

/** Live clock tick for the running-timer panel. */
const TIMER_TICK_MS = 1000;

/**
 * Safe ISO/epoch → short local date-time, or an empty string.
 *
 * @param {*} value Raw date value from an API record.
 * @returns {string} Localized `YYYY. MM. DD. HH:MM`-ish string, `''` when unparseable.
 */
const formatWhen = (value) => {
    const time = value ? new Date(value).getTime() : NaN;

    if (!Number.isFinite(time)) {
        return '';
    }

    return new Date(time).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

/**
 * Shared shell for a list panel: premium section heading with an "open the full
 * list" action, then rows or the empty state.
 *
 * @param {object} props Component props.
 * @param {string} props.icon MDI glyph for the heading.
 * @param {string} props.tone ManagementUI tone.
 * @param {string} props.title Section heading.
 * @param {string} [props.actionLabel] Right-aligned action text.
 * @param {Function} [props.onAction] Handler for the action text.
 * @param {string} props.emptyTitle Empty-state heading.
 * @param {string} [props.emptyText] Empty-state helper text.
 * @param {boolean} [props.loading] Render a spinner instead of the body.
 * @param {Array} props.items Rows to render (already sliced).
 * @param {Function} props.renderItem `(item, index) => JSX` row renderer.
 * @returns {JSX.Element} The panel.
 */
const PanelShell = ({icon, tone, title, actionLabel, onAction, emptyTitle, emptyText, loading, items, renderItem}) => {
    const m = useManagementTheme();

    return (
        <View style={styles.panel}>
            <SectionTitle title={title} icon={icon} tone={tone} eyebrow actionLabel={actionLabel} onPress={onAction} />
            {loading ? (
                <View style={[styles.panelLoading, {backgroundColor: m.colors.surface, borderColor: m.colors.border}]}>
                    <ActivityIndicator color={m.colors.primary} />
                </View>
            ) : items.length ? (
                items.map(renderItem)
            ) : (
                <EmptyState icon={icon} tone={tone} title={emptyTitle} text={emptyText} />
            )}
        </View>
    );
};

/**
 * Recent orders — the newest rows of the window the dashboard already loaded.
 *
 * @param {object} props Component props.
 * @param {Array} props.orders `data.orders` from the management provider.
 * @param {Function} props.onOpenList Opens the full orders list.
 * @returns {JSX.Element} The panel.
 */
export const RecentOrdersPanel = ({orders, onOpenList}) => {
    const {t} = useTranslation();
    const items = useMemo(() => [...(Array.isArray(orders) ? orders : [])]
        .sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0))
        .slice(0, PANEL_ROWS), [orders]);

    return (
        <PanelShell
            icon="clipboard-list-outline"
            tone="info"
            title={t('mgmt_dash_widget_recent_orders')}
            actionLabel={t('mgmt_dash_widget_view_all')}
            onAction={onOpenList}
            emptyTitle={t('mgmt_dash_widget_recent_orders_empty')}
            emptyText={t('mgmt_dash_widget_recent_orders_desc')}
            items={items}
            renderItem={(order, index) => (
                <RankedRow
                    key={String(order?._id || order?.id || order?.orderId)}
                    icon="receipt-text-outline"
                    tone="primary"
                    highlight={index === 0}
                    title={`#${order?.orderId || order?.dailyOrderNumber || '—'}`}
                    subtitle={[formatWhen(order?.createdAt), order?.status ? String(order.status) : ''].filter(Boolean).join(' · ')}
                    meta={formatCurrency(Number(order?.orderSum || order?.totalPrice || 0))}
                    onPress={onOpenList}
                />
            )}
        />
    );
};

/**
 * Open work orders — the service queue, newest first.
 *
 * @param {object} props Component props.
 * @param {Array} props.workOrders `data.workOrders` from the provider.
 * @param {Function} props.isOpen `(workOrder) => boolean` open-state predicate (owned by the screen).
 * @param {Function} props.onOpenList Opens the full work-order list.
 * @returns {JSX.Element} The panel.
 */
export const OpenWorkOrdersPanel = ({workOrders, isOpen, onOpenList}) => {
    const {t} = useTranslation();
    const items = useMemo(() => (Array.isArray(workOrders) ? workOrders : [])
        .filter((workOrder) => (typeof isOpen === 'function' ? isOpen(workOrder) : true))
        .sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0))
        .slice(0, PANEL_ROWS), [workOrders, isOpen]);

    return (
        <PanelShell
            icon="clipboard-text-clock-outline"
            tone="warning"
            title={t('mgmt_dash_widget_work_order_queue')}
            actionLabel={t('mgmt_dash_widget_view_all')}
            onAction={onOpenList}
            emptyTitle={t('mgmt_dash_widget_work_order_queue_empty')}
            emptyText={t('mgmt_dash_widget_work_order_queue_desc')}
            items={items}
            renderItem={(workOrder) => (
                <ListRow
                    key={String(workOrder?._id || workOrder?.id)}
                    icon="tools"
                    iconTone="warning"
                    title={workOrder?.title || workOrder?.subject || workOrder?.workOrderNumber || t('mgmt_dash_stat_work_order')}
                    subtitle={workOrder?.customerName || workOrder?.customer?.name || formatWhen(workOrder?.createdAt)}
                    badge={workOrder?.status ? {label: String(workOrder.status), tone: 'warning'} : null}
                    onPress={onOpenList}
                />
            )}
        />
    );
};

/**
 * Recent quotes — the CRM's live offers with their status chip.
 *
 * @param {object} props Component props.
 * @param {Array} props.quotes `data.quotes` from the provider.
 * @param {Function} props.onOpenList Opens the full quote list.
 * @returns {JSX.Element} The panel.
 */
export const RecentQuotesPanel = ({quotes, onOpenList}) => {
    const {t} = useTranslation();
    const items = useMemo(() => [...(Array.isArray(quotes) ? quotes : [])]
        .sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0))
        .slice(0, PANEL_ROWS), [quotes]);

    return (
        <PanelShell
            icon="file-document-edit-outline"
            tone="primary"
            title={t('mgmt_dash_widget_recent_quotes')}
            actionLabel={t('mgmt_dash_widget_view_all')}
            onAction={onOpenList}
            emptyTitle={t('mgmt_dash_widget_recent_quotes_empty')}
            emptyText={t('mgmt_dash_widget_recent_quotes_desc')}
            items={items}
            renderItem={(quote, index) => (
                <RankedRow
                    key={String(quote?._id || quote?.id)}
                    icon="file-document-outline"
                    tone="primary"
                    highlight={index === 0}
                    title={quote?.title || quote?.quoteNumber || t('mgmt_dash_stat_quote')}
                    subtitle={[quote?.customerName || quote?.customer?.name || quote?.lead?.company || formatWhen(quote?.createdAt), quote?.status ? String(quote.status) : ''].filter(Boolean).join(' · ')}
                    meta={formatCurrency(Number(quote?.grandTotal || quote?.total || quote?.netTotal || 0))}
                    onPress={onOpenList}
                />
            )}
        />
    );
};

/**
 * Recent leads — the top of the pipeline.
 *
 * @param {object} props Component props.
 * @param {Array} props.leads `data.leads` from the provider.
 * @param {Function} props.onOpenList Opens the full lead list.
 * @returns {JSX.Element} The panel.
 */
export const RecentLeadsPanel = ({leads, onOpenList}) => {
    const {t} = useTranslation();
    const items = useMemo(() => [...(Array.isArray(leads) ? leads : [])]
        .sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0))
        .slice(0, PANEL_ROWS), [leads]);

    return (
        <PanelShell
            icon="account-tie-outline"
            tone="violet"
            title={t('mgmt_dash_widget_recent_leads')}
            actionLabel={t('mgmt_dash_widget_view_all')}
            onAction={onOpenList}
            emptyTitle={t('mgmt_dash_widget_recent_leads_empty')}
            emptyText={t('mgmt_dash_widget_recent_leads_desc')}
            items={items}
            renderItem={(lead) => (
                <ListRow
                    key={String(lead?._id || lead?.id)}
                    icon="account-tie-outline"
                    iconTone="violet"
                    title={lead?.company || lead?.name || t('mgmt_dash_stat_lead')}
                    subtitle={lead?.company && lead?.name ? lead.name : (lead?.email || lead?.phone || formatWhen(lead?.createdAt))}
                    badge={lead?.status ? {label: String(lead.status), tone: 'violet'} : null}
                    onPress={onOpenList}
                />
            )}
        />
    );
};

/**
 * Stock shortage — the items at or below zero, worst first.
 *
 * @param {object} props Component props.
 * @param {Array} props.stock `data.stock` from the provider.
 * @param {Function} props.isLow `(item) => boolean` shortage predicate (owned by the screen).
 * @param {Function} props.onOpenList Opens the full stock list.
 * @returns {JSX.Element} The panel.
 */
export const LowStockPanel = ({stock, isLow, onOpenList}) => {
    const {t} = useTranslation();
    const quantityOf = (item) => Number(item?.stockQty ?? item?.stock ?? 0);
    const items = useMemo(() => (Array.isArray(stock) ? stock : [])
        .filter((item) => (typeof isLow === 'function' ? isLow(item) : true))
        .sort((a, b) => quantityOf(a) - quantityOf(b))
        .slice(0, PANEL_ROWS), [stock, isLow]);

    return (
        <PanelShell
            icon="package-variant-closed"
            tone="danger"
            title={t('mgmt_dash_widget_low_stock_list')}
            actionLabel={t('mgmt_dash_widget_view_all')}
            onAction={onOpenList}
            emptyTitle={t('mgmt_dash_widget_low_stock_empty')}
            emptyText={t('mgmt_dash_widget_low_stock_list_desc')}
            items={items}
            renderItem={(item) => (
                <ListRow
                    key={String(item?._id || item?.id || item?.name)}
                    icon="package-variant-closed"
                    iconTone="danger"
                    title={item?.name || item?.title || '—'}
                    subtitle={item?.warehouseName || item?.warehouse?.name || item?.unit || ''}
                    meta={formatNumber(quantityOf(item))}
                    onPress={onOpenList}
                />
            )}
        />
    );
};

/**
 * Time tracker — the caller's own clock.
 *
 * Shows the live elapsed time of a running/paused/break timer (ticking every
 * second, like the Time Tracker screen), plus today's and this week's totals.
 * Idle shows the totals and invites the user into the tracker.
 *
 * Fetches on mount and whenever `refreshToken` changes (the dashboard passes
 * its own `lastUpdatedAt`, so pull-to-refresh refreshes this panel too).
 *
 * @param {object} props Component props.
 * @param {object} props.scope Active management scope (`{restaurantId}`).
 * @param {*} [props.refreshToken] Changing value that re-triggers the fetch.
 * @param {Function} props.onOpen Opens the Time Tracker screen.
 * @returns {JSX.Element} The panel.
 */
export const TimeTrackerPanel = ({scope, refreshToken, onOpen}) => {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const [state, setState] = useState({loading: true, timer: null, day: 0, week: 0});
    const [elapsed, setElapsed] = useState(0);
    const timerRef = useRef(null);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            const [tracker, summary] = await Promise.all([
                fetchTrackerState(scope),
                fetchTimeSummary(scope),
            ]);

            if (cancelled) {
                return;
            }

            setState({
                loading: false,
                timer: tracker?.timer || null,
                day: Number(summary?.day?.totalSeconds || 0),
                week: Number(summary?.week?.totalSeconds || 0),
            });
        })();

        return () => {
            cancelled = true;
        };
    }, [scope, refreshToken]);

    // Local 1 s tick so the clock reads live; a `break`/`paused` timer freezes
    // because resolveTrackerElapsedSeconds stops advancing for those states.
    useEffect(() => {
        if (!state.timer) {
            setElapsed(0);

            return undefined;
        }

        const tick = () => setElapsed(resolveTrackerElapsedSeconds(state.timer));

        tick();
        timerRef.current = globalThis.setInterval(tick, TIMER_TICK_MS);

        return () => globalThis.clearInterval(timerRef.current);
    }, [state.timer]);

    const trackerState = resolveTrackerState(state.timer);
    const labels = resolveTrackerLabels(state.timer);
    const units = {hour: t('tt_unit_hour', {defaultValue: 'ó'}), minute: t('tt_unit_minute', {defaultValue: 'p'})};
    const stateTone = trackerState === 'running' ? 'success' : trackerState === 'break' ? 'warning' : trackerState === 'paused' ? 'info' : 'neutral';
    const stateLabel = t(`mgmt_dash_widget_timer_state_${trackerState}`);

    return (
        <View style={styles.panel}>
            <SectionTitle title={t('mgmt_data_time_tracking_title')} icon="timer-outline" tone="violet" eyebrow actionLabel={t('mgmt_dash_widget_open')} onPress={onOpen} />
            <ChartCard icon="timer-outline" tone="violet" title={t('mgmt_dash_widget_time_tracker')} subtitle={t('mgmt_dash_widget_time_tracker_desc')}>
                {state.loading ? (
                    <ActivityIndicator color={m.colors.primary} />
                ) : (
                    <>
                        <View style={styles.timerHeader}>
                            <View style={{flex: 1}}>
                                {!!labels.eyebrow && <Text style={[styles.timerEyebrow, {color: m.colors.subText}]} numberOfLines={1}>{labels.eyebrow}</Text>}
                                <Text style={[styles.timerHeadline, {color: m.colors.heading}]} numberOfLines={1}>
                                    {labels.headline || t('mgmt_dash_widget_timer_idle_title')}
                                </Text>
                            </View>
                            <StatusBadge label={stateLabel} tone={stateTone} />
                        </View>
                        <Text style={[styles.timerClock, {color: trackerState === 'idle' ? m.colors.subText : m.colors.heading}]}>
                            {formatTrackerClock(state.timer ? elapsed : 0)}
                        </Text>
                        <View style={styles.timerTotals}>
                            <View style={styles.timerTotal}>
                                <Text style={[styles.timerTotalLabel, {color: m.colors.subText}]}>{t('mgmt_dash_widget_timer_today')}</Text>
                                <Text style={[styles.timerTotalValue, {color: m.colors.heading}]}>{formatTrackerDuration(state.day, units)}</Text>
                            </View>
                            <View style={styles.timerTotal}>
                                <Text style={[styles.timerTotalLabel, {color: m.colors.subText}]}>{t('mgmt_dash_widget_timer_week')}</Text>
                                <Text style={[styles.timerTotalValue, {color: m.colors.heading}]}>{formatTrackerDuration(state.week, units)}</Text>
                            </View>
                            <TouchableOpacity onPress={onOpen} activeOpacity={0.85} style={[styles.timerCta, {borderColor: m.colors.border, backgroundColor: m.colors.surfaceAlt}]}>
                                <Icon name={state.timer ? 'arrow-right' : 'play'} size={16} color={m.colors.primary} />
                                <Text style={[styles.timerCtaText, {color: m.colors.primary}]} numberOfLines={1}>
                                    {state.timer ? t('mgmt_dash_widget_open') : t('mgmt_dash_widget_timer_start')}
                                </Text>
                            </TouchableOpacity>
                        </View>
                    </>
                )}
            </ChartCard>
        </View>
    );
};

/**
 * Meeting transcripts — the caller's own latest recordings and their state.
 *
 * @param {object} props Component props.
 * @param {*} [props.refreshToken] Changing value that re-triggers the fetch.
 * @param {Function} props.onOpen Opens the Meeting Recorder screen.
 * @param {Function} props.onOpenMeeting `(id) => void` opens one meeting's detail.
 * @returns {JSX.Element} The panel.
 */
export const MeetingsPanel = ({refreshToken, onOpen, onOpenMeeting}) => {
    const {t} = useTranslation();
    const [state, setState] = useState({loading: true, items: []});

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const response = await listMeetings({mine: true, limit: PANEL_ROWS, page: 1});
                const items = Array.isArray(response?.items) ? response.items : [];

                if (!cancelled) {
                    setState({loading: false, items});
                }
            } catch (_error) {
                // Fail-soft: an offline/unauthorized read renders the empty state.
                if (!cancelled) {
                    setState({loading: false, items: []});
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [refreshToken]);

    const toneForStatus = useCallback((status) => {
        const value = String(status || '').toLowerCase();

        if (value === 'ready' || value === 'completed' || value === 'done') {
            return 'success';
        }
        if (value === 'processing' || value === 'transcribing' || value === 'uploading') {
            return 'info';
        }
        if (value === 'failed' || value === 'error') {
            return 'danger';
        }

        return 'neutral';
    }, []);

    return (
        <PanelShell
            icon="microphone-outline"
            tone="info"
            title={t('mgmt_data_meetings_title')}
            actionLabel={t('mgmt_dash_widget_open')}
            onAction={onOpen}
            emptyTitle={t('mgmt_dash_widget_meetings_empty')}
            emptyText={t('mgmt_dash_widget_meetings_desc')}
            loading={state.loading}
            items={state.items}
            renderItem={(meeting) => (
                <ListRow
                    key={String(meeting?._id || meeting?.id)}
                    icon="microphone-outline"
                    iconTone="info"
                    title={meeting?.title || t('mgmt_data_meetings_title')}
                    subtitle={formatWhen(meeting?.startedAt || meeting?.createdAt)}
                    badge={meeting?.status ? {label: String(meeting.status), tone: toneForStatus(meeting.status)} : null}
                    onPress={() => onOpenMeeting?.(String(meeting?._id || meeting?.id))}
                />
            )}
        />
    );
};

const styles = StyleSheet.create({
    panel: {marginBottom: 4},
    panelLoading: {borderRadius: 18, borderWidth: StyleSheet.hairlineWidth, paddingVertical: 28, alignItems: 'center'},

    timerHeader: {flexDirection: 'row', alignItems: 'center', marginBottom: 6},
    timerEyebrow: {fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.6},
    timerHeadline: {fontSize: 15, fontWeight: '800', marginTop: 2},
    timerClock: {fontSize: 34, fontWeight: '900', letterSpacing: 1, fontVariant: ['tabular-nums'], marginVertical: 6},
    timerTotals: {flexDirection: 'row', alignItems: 'center', gap: 14, marginTop: 4},
    timerTotal: {minWidth: 72},
    timerTotalLabel: {fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5},
    timerTotalValue: {fontSize: 16, fontWeight: '800', marginTop: 2},
    timerCta: {marginLeft: 'auto', flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 14, paddingVertical: 9, borderRadius: 999, borderWidth: StyleSheet.hairlineWidth},
    timerCtaText: {fontSize: 13, fontWeight: '800'},
});

export default {
    LowStockPanel,
    MeetingsPanel,
    OpenWorkOrdersPanel,
    RecentLeadsPanel,
    RecentOrdersPanel,
    RecentQuotesPanel,
    TimeTrackerPanel,
};
